import React from "react";
import styled from "styled-components";

import PageHero from "../../components/page-hero/PageHero";
import MarkdownContent from "../../components/markdown/MarkdownContent";
// @ts-expect-error - JSXファイルの型定義なし
import OsFooter from "../os/components/OsFooter";
import useDocumentTitle from "../../hooks/useDocumentTitle";

import privacyPolicyMd from "./content/privacy-policy.md?raw";
import termsOfServiceMd from "./content/terms-of-service.md?raw";
import tokushohoMd from "./content/tokushoho.md?raw";

/**
 * 法務系の公開静的ページ（旧 Django SSR `/privacypolicy` `/termsofservice`
 * `/tokushoho` の SPA 移植）。
 *
 * 本文は `content/*.md` に Markdown で保持し、共有ノートと同じ
 * MarkdownContent（document バリアント）で描画する。条文は契約の一部を
 * 構成するため、URL はそのまま固定し、内容の変更はリポジトリで履歴管理する。
 */
export type LegalDocumentKey = "privacy-policy" | "terms-of-service" | "tokushoho";

interface LegalDocument {
  /** 英語タイトル（PageHero の主タイトル / document.title） */
  title: string;
  /** 日本語タイトル（PageHero のサブタイトル） */
  subtitle: string;
  content: string;
}

const DOCUMENTS: Record<LegalDocumentKey, LegalDocument> = {
  "privacy-policy": {
    title: "Privacy Policy",
    subtitle: "プライバシーポリシー",
    content: privacyPolicyMd,
  },
  "terms-of-service": {
    title: "Terms of Service",
    subtitle: "利用規約",
    content: termsOfServiceMd,
  },
  tokushoho: {
    title: "Legal Notice",
    subtitle: "特定商取引法に基づく表示",
    content: tokushohoMd,
  },
};

export interface LegalPageProps {
  document: LegalDocumentKey;
}

const LegalMain = styled.main`
  position: fixed;
  inset: 0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const LegalScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const LegalArticle = styled.article.attrs({
  className: "u-wrapper-reading u-mx-auto u-pt32",
})`
  /* フッターまでの余白。u-pb は 32 までしか無いので局所で持つ */
  padding-bottom: 96px;

  /* プライバシーポリシー第2条の表。短い語句の列（(n) / 取得時）が
     1 文字ずつ折れないよう最小幅を確保する */
  & td:first-child {
    white-space: nowrap;
  }
  & td:nth-child(3) {
    min-width: 9em;
  }
`;

const LegalPage: React.FC<LegalPageProps> = ({ document }) => {
  const doc = DOCUMENTS[document];
  useDocumentTitle(doc.subtitle);

  return (
    <LegalMain>
      <LegalScrollArea>
        <PageHero title={doc.title} subtitle={doc.subtitle} />
        <LegalArticle>
          <MarkdownContent content={doc.content} variant="document" />
        </LegalArticle>
        <OsFooter />
      </LegalScrollArea>
    </LegalMain>
  );
};

export default LegalPage;
